import { useEffect } from 'react';
import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals';

interface AnalyticsEvent {
  name: string;
  delta: number;
  value: number;
  id: string;
  rating: 'good' | 'needs-improvement' | 'poor';
}

interface PageViewEvent {
  page: string;
  title: string;
  timestamp: number;
  userAgent: string;
  referrer: string;
}

interface UserInteractionEvent {
  type: 'click' | 'scroll' | 'navigation' | 'form_submit';
  element?: string;
  section?: string;
  timestamp: number;
}

// Google Analytics 4 integration
declare global {
  interface Window {
    gtag: any;
    dataLayer: any[];
  }
}

class AnalyticsService {
  private isEnabled: boolean = true;
  private sessionId: string;
  private userId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = this.getUserId();
    this.setupGoogleAnalytics();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getUserId(): string {
    let userId = localStorage.getItem('portfolio_user_id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('portfolio_user_id', userId);
    }
    return userId;
  }

  private setupGoogleAnalytics(): void {
    // Google Analytics 4 setup
    const GA_MEASUREMENT_ID = 'G-XXXXXXXXXX'; // Replace with your GA4 ID
    
    if (typeof window !== 'undefined' && this.isEnabled) {
      // Load GA4 script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      // Configure GA4
      window.gtag('js', new Date());
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href,
        custom_map: {
          custom_parameter_1: 'session_id',
          custom_parameter_2: 'user_type'
        }
      });
    }
  }

  // Track page views
  trackPageView(page: string, title: string): void {
    if (!this.isEnabled) return;

    const pageViewEvent: PageViewEvent = {
      page,
      title,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      referrer: document.referrer
    };

    // Google Analytics
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: title,
        page_location: page,
        session_id: this.sessionId,
        user_id: this.userId
      });
    }

    // Local storage for debugging
    this.logEvent('page_view', pageViewEvent);
  }

  // Track user interactions
  trackInteraction(event: UserInteractionEvent): void {
    if (!this.isEnabled) return;

    if (window.gtag) {
      window.gtag('event', event.type, {
        event_category: 'engagement',
        event_label: event.element || event.section,
        session_id: this.sessionId,
        timestamp: event.timestamp
      });
    }

    this.logEvent('interaction', event);
  }

  // Track Web Vitals
  trackWebVital(metric: AnalyticsEvent): void {
    if (!this.isEnabled) return;

    if (window.gtag) {
      window.gtag('event', metric.name, {
        event_category: 'Web Vitals',
        event_label: metric.id,
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        custom_parameter_1: this.sessionId,
        custom_parameter_2: metric.rating
      });
    }

    this.logEvent('web_vital', metric);
  }

  // Track custom events
  trackCustomEvent(eventName: string, parameters: Record<string, any>): void {
    if (!this.isEnabled) return;

    if (window.gtag) {
      window.gtag('event', eventName, {
        ...parameters,
        session_id: this.sessionId,
        user_id: this.userId,
        timestamp: Date.now()
      });
    }

    this.logEvent('custom', { eventName, parameters });
  }

  // Track form submissions
  trackFormSubmission(formName: string, success: boolean): void {
    this.trackCustomEvent('form_submit', {
      form_name: formName,
      success: success,
      event_category: 'engagement'
    });
  }

  // Track project interactions
  trackProjectView(projectName: string): void {
    this.trackCustomEvent('project_view', {
      project_name: projectName,
      event_category: 'portfolio'
    });
  }

  // Track navigation
  trackNavigation(section: string): void {
    this.trackInteraction({
      type: 'navigation',
      section,
      timestamp: Date.now()
    });
  }

  // Track scroll depth
  trackScrollDepth(percentage: number): void {
    if (percentage % 25 === 0) { // Track every 25%
      this.trackCustomEvent('scroll_depth', {
        percentage,
        event_category: 'engagement'
      });
    }
  }

  // Local logging for development
  private logEvent(type: string, data: any): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Analytics] ${type}:`, data);
    }

    // Store recent events for debugging
    const events = JSON.parse(localStorage.getItem('portfolio_analytics') || '[]');
    events.push({ type, data, timestamp: Date.now() });
    
    // Keep only last 50 events
    if (events.length > 50) {
      events.splice(0, events.length - 50);
    }
    
    localStorage.setItem('portfolio_analytics', JSON.stringify(events));
  }

  // Get analytics data for admin dashboard
  getAnalyticsData(): any[] {
    return JSON.parse(localStorage.getItem('portfolio_analytics') || '[]');
  }

  // Disable/enable tracking
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    localStorage.setItem('analytics_enabled', enabled.toString());
  }
}

// Singleton instance
const analyticsService = new AnalyticsService();

// Custom hook for analytics
export const useAnalytics = () => {
  useEffect(() => {
    // Track page view on mount
    analyticsService.trackPageView(window.location.pathname, document.title);

    // Setup Web Vitals tracking
    onCLS(analyticsService.trackWebVital);
    // INP replaces FID in newer web-vitals versions
    onINP(analyticsService.trackWebVital);
    onFCP(analyticsService.trackWebVital);
    onLCP(analyticsService.trackWebVital);
    onTTFB(analyticsService.trackWebVital);

    // Track scroll depth
    let maxScrollDepth = 0;
    const handleScroll = () => {
      const scrollDepth = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollDepth > maxScrollDepth) {
        maxScrollDepth = scrollDepth;
        analyticsService.trackScrollDepth(scrollDepth);
      }
    };

    // Track time on page
    const startTime = Date.now();
    const handleBeforeUnload = () => {
      const timeOnPage = Date.now() - startTime;
      analyticsService.trackCustomEvent('time_on_page', {
        duration: Math.round(timeOnPage / 1000), // in seconds
        page: window.location.pathname
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return {
    trackPageView: analyticsService.trackPageView.bind(analyticsService),
    trackInteraction: analyticsService.trackInteraction.bind(analyticsService),
    trackCustomEvent: analyticsService.trackCustomEvent.bind(analyticsService),
    trackFormSubmission: analyticsService.trackFormSubmission.bind(analyticsService),
    trackProjectView: analyticsService.trackProjectView.bind(analyticsService),
    trackNavigation: analyticsService.trackNavigation.bind(analyticsService),
    getAnalyticsData: analyticsService.getAnalyticsData.bind(analyticsService),
    setEnabled: analyticsService.setEnabled.bind(analyticsService)
  };
};

export default analyticsService;